import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { ORGANES } from '@/mocks/organes-memory-data';
import { HEALTH_TIPS } from '@/mocks/jeux-data';
import type { OrganeData } from '@/types/games';

type Mode = 'Facile' | 'Moyen' | 'Difficile' | 'Apprentissage' | 'Challenge';

export default function PuzzleCorpsHumain() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors } = useGame();
  const [mode, setMode] = useState<Mode | null>(null);
  const [placed, setPlaced] = useState<string[]>([]);
  const [dragged, setDragged] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState<string | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const remainingOrgans = mode ? ORGANES.filter(o => !placed.includes(o.id)) : [];
  const showLabels = mode === 'Facile' || mode === 'Apprentissage';
  const showSnap = mode !== 'Difficile' && mode !== 'Challenge';

  const handleStart = (m: Mode) => {
    setMode(m);
    setPlaced([]);
    setShowGameOver(false);
    setFeedback(null);
    startGame('puzzle-corps-humain');
  };

  const handleDragStart = (organId: string) => {
    setDragged(organId);
  };

  const handleDrop = useCallback((e: React.DragEvent, targetOrganId: string) => {
    e.preventDefault();
    if (!dragged) return;
    const organ = ORGANES.find(o => o.id === dragged);
    if (!organ) return;

    const rect = dropRef.current?.getBoundingClientRect();
    if (!rect) return;

    const dropX = ((e.clientX - rect.left) / rect.width) * 100;
    const dropY = ((e.clientY - rect.top) / rect.height) * 100;

    if (dragged === targetOrganId && showSnap) {
      const dist = Math.sqrt((dropX - organ.position.x) ** 2 + (dropY - organ.position.y) ** 2);
      if (dist < 25) {
        setPlaced(prev => [...prev, dragged]);
        const bonus = mode === 'Challenge' ? 200 : mode === 'Difficile' ? 150 : 100;
        updateScore(gameScore + bonus);
        setFeedback(`Bravo ! ${organ.name} placé correctement.`);
        setTimeout(() => setFeedback(null), 2000);

        if (placed.length + 1 === ORGANES.length) {
          const timeBonus = Math.max(0, 600 - gameTime) * 2;
          const finalScore = gameScore + bonus + timeBonus;
          updateScore(finalScore);
          setTimeout(() => {
            endGame(finalScore, gameTime, gameErrors);
            setShowGameOver(true);
          }, 500);
        }
      } else {
        if (!showLabels) {
          updateErrors(gameErrors + 1);
          setFeedback('Essaie encore ! Place cet organe au bon endroit.');
          setTimeout(() => setFeedback(null), 1500);
        }
      }
    }
    setDragged(null);
  }, [dragged, placed, showSnap, showLabels, mode, gameScore, gameTime, gameErrors, updateScore, updateErrors, endGame]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleReplay = () => {
    setPlaced([]);
    setShowGameOver(false);
    setFeedback(null);
    startGame('puzzle-corps-humain');
  };

  const handleQuit = () => {
    endGame(gameScore, gameTime, gameErrors);
    navigate('/jeux', { replace: true });
  };

  if (!mode) {
    return (
      <GameWrapper gameName="Puzzle du Corps Humain" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-body-scan-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Puzzle du Corps Humain</h2>
            <p className="text-foreground-600 text-sm mb-6">Place les organes au bon endroit !</p>
            <div className="grid grid-cols-2 gap-3">
              {(['Facile', 'Moyen', 'Difficile', 'Apprentissage', 'Challenge'] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => handleStart(m)}
                  className="py-3 px-4 bg-secondary-100 hover:bg-secondary-200 rounded-xl text-sm font-medium text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </GameWrapper>
    );
  }

  const infoOrgan = showInfo ? ORGANES.find(o => o.id === showInfo) : null;

  return (
    <GameWrapper gameName="Puzzle du Corps Humain" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 max-w-7xl mx-auto w-full">
        {/* Body silhouette */}
        <div
          ref={dropRef}
          className="relative flex-1 min-h-[500px] lg:min-h-[600px] bg-background-100 rounded-3xl border-2 border-dashed border-background-300/60 flex items-center justify-center overflow-hidden"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, '')}
        >
          {/* Silhouette background */}
          <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none">
            <i className="ri-body-scan-line text-[300px] text-foreground-950"></i>
          </div>

          {/* Organ targets */}
          {ORGANES.map((organ) => {
            const isPlaced = placed.includes(organ.id);
            return (
              <div
                key={organ.id}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition-all duration-300 ${
                  isPlaced
                    ? 'border-green-400 bg-green-100/80 scale-100'
                    : showLabels
                      ? 'border-dashed border-foreground-300/60 bg-background-50/50'
                      : 'border-transparent bg-transparent'
                }`}
                style={{ left: `${organ.position.x}%`, top: `${organ.position.y}%` }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, organ.id)}
              >
                <div className={`flex flex-col items-center justify-center p-1.5 transition-all ${
                  isPlaced ? 'w-16 h-16 md:w-20 md:h-20' : 'w-14 h-14 md:w-18 md:h-18'
                }`}>
                  {isPlaced ? (
                    <div className="text-center cursor-pointer" onClick={() => setShowInfo(organ.id)}>
                      <i className="ri-checkbox-circle-fill text-green-500 text-xl md:text-2xl"></i>
                      {showLabels && <span className="text-[10px] md:text-xs font-medium text-green-700 block">{organ.name}</span>}
                    </div>
                  ) : showLabels ? (
                    <div className="text-center opacity-50">
                      <i className="ri-add-circle-line text-foreground-400 text-lg"></i>
                      <span className="text-[10px] md:text-xs text-foreground-500 block">{organ.name}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}

          {feedback && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium animate-in">
              {feedback}
            </div>
          )}
        </div>

        {/* Organ bank */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">Organes à placer ({remainingOrgans.length})</h3>
          <div className="flex flex-row lg:flex-col gap-2 flex-wrap lg:flex-nowrap max-h-32 lg:max-h-[550px] overflow-y-auto">
            {remainingOrgans.map((organ) => (
              <div
                key={organ.id}
                draggable
                onDragStart={() => handleDragStart(organ.id)}
                className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-xl px-3 py-2 cursor-grab active:cursor-grabbing hover:border-primary-500/50 hover:bg-primary-50 transition-all text-sm"
              >
                <i className="ri-draggable text-foreground-500"></i>
                <span className="font-medium text-foreground-950 whitespace-nowrap text-xs md:text-sm">{organ.name}</span>
              </div>
            ))}
            {remainingOrgans.length === 0 && (
              <p className="text-sm text-foreground-500 text-center py-4">Tous les organes sont placés !</p>
            )}
          </div>
        </div>
      </div>

      {/* Info modal */}
      {infoOrgan && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowInfo(null)}>
          <div className="bg-background-50 rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-foreground-950 text-lg">{infoOrgan.name}</h3>
              <button onClick={() => setShowInfo(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">{infoOrgan.description}</p>
            <span className="text-xs bg-accent-100 text-accent-800 px-3 py-1 rounded-full">{infoOrgan.category}</span>
          </div>
        </div>
      )}

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="puzzle-corps-humain"
          message="Anatomie maîtrisée !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}